import { create } from 'zustand';
import { http } from '@api/http';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  xpReward: number;
}

export interface PlayerStats {
  wallet: string;
  xp: number;
  level: number;
  eloRating: number;
  totalWins: number;
  totalLosses: number;
  totalGames: number;
  currentStreak: number;
  bestStreak: number;
  rankTier: number;
  rankName: string;
  globalRank: number;
  achievements: string[];
  loginStreak: number;
}

export interface ProgressionUpdate {
  wallet: string;
  xpGained: number;
  xpTotal: number;
  previousLevel: number;
  newLevel: number;
  leveledUp: boolean;
  previousElo: number;
  newElo: number;
  eloDelta: number;
  previousStreak: number;
  newStreak: number;
  streakMultiplier: number;
  previousRank: number;
  newRank: number;
  rankChanged: boolean;
  achievementsUnlocked: Achievement[];
  xpToNextLevel: number;
  badBeatBonus?: boolean; // True if loser received XP consolation for a close loss
}

export interface LeaderboardEntry {
  rank: number;
  wallet: string;
  level: number;
  xp: number;
  eloRating: number;
  totalWins: number;
  totalGames: number;
  currentStreak: number;
  bestStreak: number;
  rankTier: number;
  rankName: string;
  lastPlayedAt: string | null;
}

interface PlayerStatsState {
  stats: PlayerStats | null;
  leaderboard: LeaderboardEntry[];
  loading: boolean;
  leaderboardLoading: boolean;
  error: string | null;
  lastProgressionUpdate: ProgressionUpdate | null;

  fetchStats: (wallet: string) => Promise<void>;
  fetchLeaderboard: (type?: 'elo' | 'wins' | 'level', limit?: number) => Promise<void>;
  handleProgressionUpdate: (update: ProgressionUpdate) => void;
  claimDailyBonus: (wallet: string) => Promise<{ claimed: boolean; xpAwarded?: number }>;
  clearStats: () => void;
}

const RANK_NAMES = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Champion'];

export const getRankName = (tier: number): string => RANK_NAMES[tier] ?? 'Bronze';

export const getRankColor = (tier: number): string => {
  const colors = [
    'text-amber-600', // Bronze
    'text-slate-400', // Silver
    'text-yellow-400', // Gold
    'text-cyan-400', // Platinum
    'text-blue-400', // Diamond
    'text-purple-400', // Champion
  ];
  return colors[tier] ?? colors[0];
};

export const getRankBgColor = (tier: number): string => {
  const colors = [
    'bg-amber-600/20 border-amber-600/50', // Bronze
    'bg-slate-400/20 border-slate-400/50', // Silver
    'bg-yellow-400/20 border-yellow-400/50', // Gold
    'bg-cyan-400/20 border-cyan-400/50', // Platinum
    'bg-blue-400/20 border-blue-400/50', // Diamond
    'bg-purple-400/20 border-purple-400/50', // Champion
  ];
  return colors[tier] ?? colors[0];
};

export const getXPForLevel = (level: number): number => {
  return Math.floor(100 * Math.pow(level, 1.5));
};

export const usePlayerStatsStore = create<PlayerStatsState>((set, get) => ({
  stats: null,
  leaderboard: [],
  loading: false,
  leaderboardLoading: false,
  error: null,
  lastProgressionUpdate: null,

  fetchStats: async (wallet: string) => {
    if (!wallet) return;

    set({ loading: true, error: null });
    try {
      const response = await http.get<{ stats: PlayerStats }>(`/players/${wallet}/stats`);
      set({ stats: response.stats, loading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch stats';
      set({ error: message, loading: false });
    }
  },

  fetchLeaderboard: async (type = 'elo', limit = 100) => {
    set({ leaderboardLoading: true });
    try {
      const response = await http.get<{ leaderboard: LeaderboardEntry[] }>(
        `/leaderboard?type=${type}&limit=${limit}`,
      );
      set({ leaderboard: response.leaderboard, leaderboardLoading: false });
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      set({ leaderboardLoading: false });
    }
  },

  handleProgressionUpdate: (update: ProgressionUpdate) => {
    const currentStats = get().stats;
    if (!currentStats || currentStats.wallet !== update.wallet) return;

    // Update stats with new values
    set({
      stats: {
        ...currentStats,
        xp: update.xpTotal,
        level: update.newLevel,
        eloRating: update.newElo,
        currentStreak: update.newStreak,
        rankTier: update.newRank,
        rankName: getRankName(update.newRank),
        totalWins:
          update.newStreak > update.previousStreak
            ? currentStats.totalWins + 1
            : currentStats.totalWins,
        totalGames: currentStats.totalGames + 1,
        achievements: [
          ...currentStats.achievements,
          ...update.achievementsUnlocked.map((a) => a.id),
        ],
      },
      lastProgressionUpdate: update,
    });

    // Dispatch events for celebration effects
    if (update.leveledUp) {
      window.dispatchEvent(
        new CustomEvent('progression:levelup', {
          detail: { level: update.newLevel, xpGained: update.xpGained },
        }),
      );
    }

    if (update.rankChanged && update.newRank > update.previousRank) {
      window.dispatchEvent(
        new CustomEvent('progression:rankup', {
          detail: { rank: update.newRank, rankName: getRankName(update.newRank) },
        }),
      );
    }

    if (update.achievementsUnlocked.length > 0) {
      update.achievementsUnlocked.forEach((achievement) => {
        window.dispatchEvent(
          new CustomEvent('progression:achievement', {
            detail: achievement,
          }),
        );
      });
    }

    // Streak milestones
    if (update.newStreak === 3 || update.newStreak === 5 || update.newStreak === 10) {
      window.dispatchEvent(
        new CustomEvent('progression:streak', {
          detail: { streak: update.newStreak, multiplier: update.streakMultiplier },
        }),
      );
    }
  },

  claimDailyBonus: async (wallet: string) => {
    try {
      const response = await http.post<{
        claimed: boolean;
        xpAwarded?: number;
        loginStreak?: number;
        nextAvailableAt?: string;
      }>(`/players/${wallet}/daily-bonus`, {});

      if (response.claimed && response.xpAwarded) {
        const currentStats = get().stats;
        if (currentStats) {
          set({
            stats: {
              ...currentStats,
              xp: currentStats.xp + response.xpAwarded,
              loginStreak: response.loginStreak ?? currentStats.loginStreak,
            },
          });
        }

        window.dispatchEvent(
          new CustomEvent('progression:dailybonus', {
            detail: { xpAwarded: response.xpAwarded, loginStreak: response.loginStreak },
          }),
        );
      }

      return { claimed: response.claimed, xpAwarded: response.xpAwarded };
    } catch (error) {
      console.error('Failed to claim daily bonus:', error);
      return { claimed: false };
    }
  },

  clearStats: () => {
    set({ stats: null, error: null, lastProgressionUpdate: null });
  },
}));
